import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { TrustedCircleItem } from './TrustedCircleList'
import { PersonLiteDto } from '../../../api/person/models/PersonDto'
import { getProfilePicUrl, formatPersonName } from '../../../utils/personUtils'
import Modal from '../../common/modals/Modal'
import LoadingSpinner from '../../common/loading/LoadingSpinner'
import Button from '../../common/form/inputs/Button'
import PeoplePickerInput, {
    DirectoryUser,
} from '../../common/form/inputs/PeoplePickerInput'
import roleApi from '../../../api/role/RoleApi'
import tenantApi from '../../../api/tenant/TenantApi'
import { UserDto } from '../../../api/tenant/models/UserDto'
import UserLabel from '../../common/person/UserLabel'
import { toast } from 'sonner'
import { ShareRoleWithUserDto } from '../../../api/role/models/shareRoleWithUsers/ShareRoleWithUsersReq'
import personApi from '../../../api/person/PersonApi'
import { CsPersonSearchResultDto } from '../../../api/person/models/searchCs/SearchPersonsCsDtos'
import InviteReviewRow, {
    DecoratedPersonResult,
    PendingInviteRow,
    SearchMode,
    SearchState,
} from './InviteReviewRow'

const SEARCH_DEBOUNCE_MS = 300
const DEFAULT_SEARCH_PAGE_NUMBER = 1
const MAX_SEARCH_RESULTS_PER_PAGE = 25

const createDefaultSearchState = (): SearchState => ({
    query: '',
    mode: 'full_name',
    results: [],
    loading: false,
    error: undefined,
    hasSearched: false,
})

function isLinkedInProfileUrl(value: string): boolean {
    if (!value) return false
    const normalized = value.trim().toLowerCase()
    if (!normalized) return false
    return normalized.includes('linkedin.com/in/')
}

function buildFullName(person: CsPersonSearchResultDto): string {
    const fullName = formatPersonName({
        firstName: person.firstName,
        middleName: person.middleName,
        lastName: person.lastName,
    })

    return fullName || person.linkedInUrl || person.id
}

export default function InviteUsersModal({
    roleTitle,
    roleId,
    tenantId,
    onClose,
    sharedWith,
    personById,
    tenantCompanyIds = [],
}: {
    roleTitle: string
    roleId: string
    tenantId: string
    onClose: (shared: boolean) => void
    sharedWith: TrustedCircleItem[]
    personById: Map<string, PersonLiteDto>
    tenantCompanyIds?: string[]
}): React.JSX.Element {
    const [busy, setBusy] = useState(false)

    // Selected rows (review list) – single source of truth
    const [rows, setRows] = useState<PendingInviteRow[]>([])

    // Keep refs to inline search inputs so we can focus them when needed
    const searchInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

    const searchDebounceRefs = useRef<
        Record<string, ReturnType<typeof setTimeout> | null>
    >({})

    function focusSearchInput(email: string): void {
        const lower = email.toLowerCase()
        const input = searchInputRefs.current[lower]
        if (!input) return
        if (typeof window === 'undefined') return
        if (document.activeElement === input) return
        window.requestAnimationFrame(() => {
            input.focus({ preventScroll: true })
        })
    }

    const [searchStateByEmail, setSearchStateByEmail] = useState<
        Record<string, SearchState>
    >({})

    const tenantCompanySet = useMemo(() => {
        return new Set(
            (tenantCompanyIds ?? [])
                .map((id) => id?.trim())
                .filter((id): id is string => Boolean(id)),
        )
    }, [tenantCompanyIds])

    function getSearchState(email: string): SearchState {
        const lower = email.toLowerCase()
        return searchStateByEmail[lower] ?? createDefaultSearchState()
    }

    function mergeSearchState(
        email: string,
        patch: Partial<SearchState>,
    ): void {
        const lower = email.toLowerCase()
        setSearchStateByEmail((prev) => {
            const current = prev[lower] ?? createDefaultSearchState()
            return {
                ...prev,
                [lower]: { ...current, ...patch },
            }
        })
    }

    function deleteSearchState(email: string): void {
        const lower = email.toLowerCase()
        setSearchStateByEmail((prev) => {
            if (!(lower in prev)) {
                return prev
            }
            const next = { ...prev }
            delete next[lower]
            return next
        })
    }

    function resetSearchState(email: string): void {
        const lower = email.toLowerCase()
        setSearchStateByEmail((prev) => ({
            ...prev,
            [lower]: createDefaultSearchState(),
        }))
    }

    function clearScheduledSearch(email: string): void {
        const lower = email.toLowerCase()
        const existing = searchDebounceRefs.current[lower]
        if (existing) {
            clearTimeout(existing)
            searchDebounceRefs.current[lower] = null
        }
    }

    function scheduleSearch(email: string): void {
        const lower = email.toLowerCase()
        clearScheduledSearch(email)
        searchDebounceRefs.current[lower] = setTimeout(() => {
            searchDebounceRefs.current[lower] = null
            void handleSearchColdStorage(email, { triggeredByTyping: true })
        }, SEARCH_DEBOUNCE_MS)
    }

    // People with access (from parent)
    const accessPeople = useMemo(
        () =>
            sharedWith.map(({ user }) => {
                const person = user.personId
                    ? personById.get(user.personId)
                    : undefined
                const displayName = person
                    ? formatPersonName(person)
                    : (user.name ?? user.email ?? 'Unknown')
                return {
                    id: user.id,
                    name: displayName,
                    email: user.email as string,
                    person,
                }
            }),
        [sharedWith, personById],
    )

    // Directory suggestions (from tenant API)
    const [directory, setDirectory] = useState<DirectoryUser[]>([])
    const lookupCache = useRef<Map<string, DirectoryUser | null>>(new Map())

    useEffect(() => {
        const debounceRefsSnapshot = { ...searchDebounceRefs.current }
        return () => {
            Object.values(debounceRefsSnapshot).forEach((timeoutId) => {
                if (timeoutId) {
                    clearTimeout(timeoutId)
                }
            })
        }
    }, [])

    useEffect(() => {
        let mounted = true
        async function loadTenantUsers() {
            try {
                const res = await tenantApi.getTenantUsers(tenantId)
                if (!mounted) return
                const users = res.users
                const dir: DirectoryUser[] = users.map((u: UserDto) => {
                    const primaryMembership =
                        u.memberships?.find(
                            (membership) => membership.tenantId === tenantId,
                        ) ?? u.memberships?.[0]

                    return {
                        id: u.id,
                        name: u.person
                            ? formatPersonName(u.person)
                            : (u.name ?? u.email),
                        email: u.email,
                        avatarUrl: u.person
                            ? getProfilePicUrl(u.person, true)
                            : (u.profilePicUrl ?? undefined),
                        tenantName: primaryMembership?.tenant?.name,
                        headline: u.person?.headline,
                    }
                })
                setDirectory((prev) => {
                    const merged = new Map<string, DirectoryUser>()
                    for (const entry of prev) {
                        merged.set(entry.email.toLowerCase(), entry)
                    }
                    for (const entry of dir) {
                        const key = entry.email.toLowerCase()
                        const existing = merged.get(key)
                        merged.set(key, { ...existing, ...entry })
                    }
                    return Array.from(merged.values())
                })
            } catch (e) {
                console.error('Failed to load tenant users', e)
                setDirectory([])
            }
        }
        loadTenantUsers()
        return () => {
            mounted = false
        }
    }, [tenantId])

    // Derived state for enabling Share button
    const unresolved = rows.some(
        (r) => r.status === 'new' && !r.selectedPerson && !r.error,
    )
    const hasChecking = rows.some((r) => r.status === 'checking')
    const hasError = rows.some((r) => !!r.error)
    const canShare =
        rows.length > 0 && !unresolved && !hasError && !hasChecking && !busy

    function upsertRow(next: PendingInviteRow) {
        setRows((prev) => {
            const exists = prev.findIndex(
                (r) => r.email.toLowerCase() === next.email.toLowerCase(),
            )
            if (exists >= 0) return prev
            // Prepend new entries so they appear at the top of the Review list
            return [next, ...prev]
        })
        if (next.status !== 'existing') {
            resetSearchState(next.email)
        }
    }

    function removeByEmail(email: string) {
        setRows((prev) =>
            prev.filter((r) => r.email.toLowerCase() !== email.toLowerCase()),
        )
        // Clean up any stored ref for this email
        delete searchInputRefs.current[email.toLowerCase()]
        deleteSearchState(email)
        clearScheduledSearch(email)
    }

    function setRow(email: string, patch: Partial<PendingInviteRow>) {
        setRows((prev) =>
            prev.map((r) =>
                r.email.toLowerCase() === email.toLowerCase()
                    ? { ...r, ...patch }
                    : r,
            ),
        )
    }

    // When user clicks a suggestion from the dropdown
    function handlePickUser(user: DirectoryUser) {
        upsertRow({ email: user.email, name: user.name, status: 'existing' })
    }

    // When user pastes or presses Enter/comma/space
    async function handleTokenize(emails: string[]) {
        for (const emailRaw of emails) {
            const email = emailRaw.trim()
            if (!email) continue
            if (rows.some((r) => r.email.toLowerCase() === email.toLowerCase()))
                continue

            const lower = email.toLowerCase()
            const alreadyShared = accessPeople.some(
                (p) => p.email.toLowerCase() === lower,
            )
            if (alreadyShared) {
                toast.info(
                    'Role has already been shared with user with email: ' +
                        email,
                )
                continue
            }

            const existing = directory.find(
                (u) => u.email.toLowerCase() === email.toLowerCase(),
            )

            if (existing) {
                upsertRow({
                    email,
                    status: 'existing',
                    name: existing.name,
                })
                continue
            }

            upsertRow({
                email,
                status: 'checking',
                name: email,
                selectedPerson: undefined,
            })
            await resolveEmailAgainstDirectory(email)
        }
    }

    async function resolveEmailAgainstDirectory(email: string) {
        const lower = email.toLowerCase()
        const cached = lookupCache.current.get(lower)

        if (cached !== undefined) {
            if (cached) {
                setDirectory((prev) => upsertDirectory(prev, cached))
                setRow(email, {
                    status: 'existing',
                    name: cached.name,
                    error: undefined,
                    selectedPerson: undefined,
                })
                deleteSearchState(email)
            } else {
                setRow(email, {
                    status: 'new',
                    name: email,
                    error: undefined,
                    selectedPerson: undefined,
                })
                resetSearchState(email)
            }
            return
        }

        try {
            const res = await tenantApi.lookupUserByEmail({ email })
            const user = res.user

            if (user) {
                const personName = user.person
                    ? formatPersonName(user.person)
                    : (user.name ?? user.email)
                const avatarUrl = user.person
                    ? getProfilePicUrl(user.person, true)
                    : (user.profilePicUrl ?? undefined)
                const primaryMembership = user.memberships?.[0]
                const tenantName =
                    primaryMembership?.tenant?.name ??
                    primaryMembership?.tenantId ??
                    undefined

                const directoryUser: DirectoryUser = {
                    id: user.id,
                    name: personName,
                    email: user.email,
                    avatarUrl,
                    tenantName,
                    headline: user.person?.headline,
                }

                lookupCache.current.set(lower, directoryUser)
                setDirectory((prev) => upsertDirectory(prev, directoryUser))
                setRow(email, {
                    status: 'existing',
                    name: directoryUser.name,
                    error: undefined,
                    selectedPerson: undefined,
                })
                deleteSearchState(email)
            } else {
                lookupCache.current.set(lower, null)
                setRow(email, {
                    status: 'new',
                    name: email,
                    error: undefined,
                    selectedPerson: undefined,
                })
                resetSearchState(email)
            }
        } catch (error) {
            console.error('Failed to lookup user by email', error)
            lookupCache.current.set(lower, null)
            setRow(email, {
                status: 'error',
                error: 'Unable to verify email right now. Please try again.',
            })
        }
    }

    function handleSearchModeChange(email: string, mode: SearchMode) {
        const trimmedEmail = email.toLowerCase()
        const state = {
            ...getSearchState(email),
            mode,
            results: [],
            error: undefined,
            hasSearched: false,
        }
        setSearchStateByEmail((prev) => ({
            ...prev,
            [trimmedEmail]: state,
        }))
        setRow(email, {
            selectedPerson: undefined,
            status: 'new',
            error: undefined,
        })
        clearScheduledSearch(email)
        if (state.query.trim() && state.mode === 'full_name') {
            scheduleSearch(email)
        }
    }

    function handleSearchQueryChange(email: string, value: string) {
        const trimmed = value.trim()
        const currentState = getSearchState(email)
        const looksLinkedIn = isLinkedInProfileUrl(trimmed)
        const shouldAutoSwitchMode =
            looksLinkedIn && currentState.mode === 'full_name'

        mergeSearchState(email, {
            query: value,
            error: undefined,
            ...(shouldAutoSwitchMode
                ? { mode: 'li_url', results: [], hasSearched: false }
                : {}),
        })
        setRow(email, {
            error: undefined,
            status: 'new',
            selectedPerson: undefined,
        })
        clearScheduledSearch(email)
        const nextMode = shouldAutoSwitchMode ? 'li_url' : currentState.mode
        const shouldSchedule = Boolean(trimmed) && nextMode === 'full_name'

        if (shouldSchedule) {
            scheduleSearch(email)
        } else {
            mergeSearchState(email, {
                results: [],
                hasSearched: false,
            })
        }
        focusSearchInput(email)
    }

    async function handleSearchColdStorage(
        email: string,
        options?: { triggeredByTyping?: boolean },
    ) {
        const triggeredByTyping = options?.triggeredByTyping ?? false
        clearScheduledSearch(email)
        const state = getSearchState(email)
        const query = state.query.trim()

        if (!query) {
            clearScheduledSearch(email)
            mergeSearchState(email, {
                results: [],
                hasSearched: false,
                loading: false,
                error: triggeredByTyping ? undefined : state.error,
            })
            if (!triggeredByTyping) {
                const message =
                    'Enter a name or LinkedIn URL to search cold storage.'
                mergeSearchState(email, {
                    error: message,
                })
                setRow(email, { error: message })
            } else {
                setRow(email, { error: undefined })
            }
            focusSearchInput(email)
            return
        }

        mergeSearchState(email, {
            loading: true,
            error: undefined,
        })
        setRow(email, { error: undefined })

        const initialQuery = query

        try {
            const priorityCompanyIds = Array.from(tenantCompanySet)

            const response = await personApi.searchPersonsCsByFullName(query, {
                pageNumber: DEFAULT_SEARCH_PAGE_NUMBER,
                perPage: MAX_SEARCH_RESULTS_PER_PAGE,
                priorityCompanyIds,
            })

            // Ignore outdated responses if query changed while request was in flight
            const latestState = getSearchState(email)
            if (latestState.query.trim() !== initialQuery) {
                return
            }

            const decorated: DecoratedPersonResult[] = response.results.map(
                (person) => {
                    const fullName = buildFullName(person)
                    const isColleague = person.currentCompanyId
                        ? tenantCompanySet.has(person.currentCompanyId)
                        : false
                    return {
                        ...person,
                        fullName,
                        isColleague,
                    }
                },
            )

            decorated.sort((a, b) => {
                if (a.isColleague !== b.isColleague) {
                    return a.isColleague ? -1 : 1
                }
                return a.fullName.localeCompare(b.fullName)
            })

            const noResults = decorated.length === 0
            const message = noResults
                ? 'No cold storage records found.'
                : undefined

            mergeSearchState(email, {
                results: decorated,
                loading: false,
                error: message,
                hasSearched: true,
            })
            setRow(email, {
                status: 'new',
                error: message,
            })
        } catch (error) {
            console.error('Failed to search cold storage', error)
            const fallbackError =
                'Unable to search cold storage. Please try again.'
            mergeSearchState(email, {
                loading: false,
                error: fallbackError,
            })
            setRow(email, {
                status: 'new',
                error: fallbackError,
            })
        }

        focusSearchInput(email)
    }

    function handleSelectSearchResult(
        email: string,
        person: DecoratedPersonResult,
    ) {
        clearScheduledSearch(email)
        setRow(email, {
            selectedPerson: person,
            name: person.fullName,
            error: undefined,
            status: 'new',
        })
        mergeSearchState(email, {
            results: [],
            error: undefined,
            hasSearched: true,
        })
    }

    function handleClearSelectedPerson(email: string) {
        setRow(email, {
            selectedPerson: undefined,
            name: email,
        })
        mergeSearchState(email, {
            hasSearched: false,
            error: undefined,
        })
        const input = searchInputRefs.current[email.toLowerCase()]
        input?.focus({ preventScroll: true })
    }

    function upsertDirectory(
        current: DirectoryUser[],
        entry: DirectoryUser,
    ): DirectoryUser[] {
        const key = entry.email.toLowerCase()
        const exists = current.some((item) => item.email.toLowerCase() === key)
        if (exists) {
            return current.map((item) =>
                item.email.toLowerCase() === key ? { ...item, ...entry } : item,
            )
        }
        return [...current, entry]
    }

    async function handleShare() {
        setBusy(true)
        try {
            // Build a single batch payload for BE to handle atomically
            const usersToShareWith: ShareRoleWithUserDto[] = rows.map((r) => {
                if (r.status === 'existing') {
                    const match = directory.find(
                        (u) => u.email.toLowerCase() === r.email.toLowerCase(),
                    )
                    if (match) {
                        return { existingUserId: match.id }
                    }

                    // Fallback: treat as new if somehow not in directory
                    if (r.selectedPerson) {
                        return {
                            newUserEmail: r.email,
                            newUserCsPersonId: r.selectedPerson.id,
                        }
                    }

                    throw new Error(
                        `Missing directory entry for ${r.email} and no selected person to fall back to`,
                    )
                }

                const selected = r.selectedPerson
                if (!selected) {
                    throw new Error(
                        `No cold storage profile selected for ${r.email}`,
                    )
                }

                return {
                    newUserEmail: r.email,
                    newUserCsPersonId: selected.id,
                }
            })

            const shareRoleResponse = await roleApi.shareRoleWithUsers({
                roleId,
                usersToShareWith,
            })

            try {
                await roleApi.sendInviteToNewUsers({
                    roleId,
                    newUserIds: shareRoleResponse.sharedWithUserIds,
                })
            } catch (inviteError) {
                console.error(
                    'Failed to send invites to new users',
                    inviteError,
                )
                toast.error('Shared, but failed to send invites to some users.')
            }

            // Success: close and signal parent to refresh Trusted Circle
            onClose(true)
        } catch (e) {
            console.error('Failed to share role', e)
            toast.error('Failed to share. Please try again.')
        } finally {
            setBusy(false)
        }
    }

    // Focus helper: jump to the first unresolved (missing name) input
    function focusFirstUnresolved() {
        const first = rows.find(
            (r) => r.status === 'new' && !r.selectedPerson && !r.error,
        )
        if (!first) return
        const key = first.email.toLowerCase()
        const input = searchInputRefs.current[key]
        if (input) {
            input.focus()
            try {
                input.scrollIntoView({ behavior: 'smooth', block: 'center' })
            } catch {
                // no-op: scrollIntoView may not be supported in some environments
            }
        }
    }

    return (
        <Modal
            title={`Share '${roleTitle}'`}
            onClose={() => onClose(false)}
            size="medium"
        >
            <LoadingSpinner show={busy} fullScreen={true} />

            <div className="space-y-4">
                <section className="space-y-2">
                    <PeoplePickerInput
                        label="Add people"
                        placeholder="Invite people by email, press enter to add"
                        selected={rows}
                        onRemove={removeByEmail}
                        directory={directory}
                        onPickUser={handlePickUser}
                        onTokenize={handleTokenize}
                        excludeEmails={accessPeople.map((u) => u.email)}
                        autoFocus
                        disabled={busy}
                    />
                </section>

                {rows.length > 0 && (
                    <section className="space-y-3">
                        <h4 className="text-sm font-bold">Review</h4>
                        <div className="grid grid-cols-1 gap-3">
                            {rows.map((r) => (
                                <InviteReviewRow
                                    key={r.email}
                                    row={r}
                                    directory={directory}
                                    onRemove={removeByEmail}
                                    searchState={getSearchState(r.email)}
                                    onChangeSearchMode={handleSearchModeChange}
                                    onChangeQuery={handleSearchQueryChange}
                                    onSearch={handleSearchColdStorage}
                                    onSelectPerson={handleSelectSearchResult}
                                    onClearSelection={handleClearSelectedPerson}
                                    searchInputRefs={searchInputRefs}
                                    disableInteractions={busy}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {accessPeople.length > 0 && (
                    <section className="space-y-3">
                        <h4 className="text-sm font-bold">
                            People with access
                        </h4>
                        <div className="grid grid-cols-1 gap-2">
                            {accessPeople.map((u) => (
                                <div key={u.id} className="py-1">
                                    <UserLabel
                                        name={u.name}
                                        email={u.email}
                                        avatarUrl={
                                            u.person
                                                ? getProfilePicUrl(
                                                      u.person,
                                                      true,
                                                  )
                                                : undefined
                                        }
                                        size={36}
                                        truncate={false}
                                    />
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end w-full">
                    <Button
                        type="button"
                        style="secondary"
                        onClick={() => onClose(false)}
                        classes="w-full sm:w-auto"
                    >
                        Cancel
                    </Button>
                    {/* Wrapper captures taps when disabled and moves focus to first missing name */}
                    <div
                        className="w-full sm:w-auto"
                        onClick={() => {
                            if (!canShare) {
                                focusFirstUnresolved()
                            }
                        }}
                        role="presentation"
                    >
                        <Button
                            type="button"
                            style="primary"
                            onClick={handleShare}
                            enabled={canShare}
                            classes="w-full sm:w-auto"
                        >
                            Share
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    )
}
